import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map click events
const LocationMarker = ({ position, setPosition, setLocationName, setAddressParts = null }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      // Reverse geocode to get location name
      reverseGeocode(lat, lng, setLocationName, setAddressParts);
    },
  });

  return position ? (
    <Marker position={position} icon={customIcon} />
  ) : null;
};

// Reverse geocoding function using Nominatim - returns full address
const reverseGeocode = async (lat, lng, setLocationName, setAddressParts = null) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await response.json();
    if (data && data.display_name) {
      const address = data.address || {};
      
      // Build full address: Street/Village, Barangay, City, Province, Country
      const parts = [];
      
      // Street/Village/Subdivision
      const street = address.road || address.street || address.residential || 
                     address.village || address.suburb || address.neighbourhood || '';
      if (street) parts.push(street);
      
      // Barangay (Philippine-specific, often in suburb or neighbourhood)
      const barangay = address.suburb || address.neighbourhood || address.quarter || '';
      if (barangay && barangay !== street) parts.push(barangay);
      
      // City/Municipality
      const city = address.city || address.town || address.municipality || 
                   address.city_district || address.county || '';
      if (city) parts.push(city);
      
      // Province/State
      const province = address.state || address.province || address.region || '';
      if (province) parts.push(province);
      
      // Country
      const country = address.country || '';
      if (country) parts.push(country);
      
      const fullAddress = parts.length > 0 ? parts.join(', ') : data.display_name;
      setLocationName(fullAddress);
      
      // If callback provided, also return parsed address parts
      if (setAddressParts) {
        setAddressParts({
          street: street,
          barangay: barangay,
          city: city,
          province: province,
          country: country,
          full_address: fullAddress
        });
      }
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
};

// Search for location by name
const searchLocation = async (query) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
    );
    return await response.json();
  } catch (error) {
    console.error('Search error:', error);
    return [];
  }
};

const LocationPicker = ({ 
  onLocationSelect, 
  initialLocation = null,
  showRadius = false,
  radius = 10,
  onRadiusChange = null,
  placeholder = "Search for a location..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(initialLocation);
  const [locationName, setLocationName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRadius, setSelectedRadius] = useState(radius);
  const [addressParts, setAddressParts] = useState(null);

  // Default center (Manila, Philippines)
  const defaultCenter = [14.5995, 120.9842];

  useEffect(() => {
    if (initialLocation && initialLocation.length === 2) {
      setPosition(initialLocation);
      reverseGeocode(initialLocation[0], initialLocation[1], setLocationName, setAddressParts);
    }
  }, [initialLocation]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    const results = await searchLocation(searchQuery);
    setSearchResults(results);
    setSearching(false);
  };

  const handleSelectSearchResult = (result) => {
    const newPosition = [parseFloat(result.lat), parseFloat(result.lon)];
    setPosition(newPosition);
    setLocationName(result.display_name);
    reverseGeocode(newPosition[0], newPosition[1], setLocationName, setAddressParts);
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleConfirm = () => {
    if (position && locationName) {
      onLocationSelect({
        coordinates: position,
        name: locationName,
        addressParts: addressParts,
        radius: showRadius ? selectedRadius : null
      });
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setPosition(null);
    setLocationName('');
    onLocationSelect(null);
    setIsOpen(false);
  };

  const handleRadiusChange = (newRadius) => {
    setSelectedRadius(newRadius);
    if (onRadiusChange) {
      onRadiusChange(newRadius);
    }
  };

  return (
    <div className="relative" data-testid="location-picker">
      {/* Location Input Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="w-full px-4 py-2 text-left border rounded-lg bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
        data-testid="location-picker-button"
      >
        <span className={locationName ? 'text-gray-900' : 'text-gray-500'}>
          {locationName || placeholder}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" data-testid="location-picker-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b bg-gradient-to-r from-orange-500 to-orange-600">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Select Location</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:text-gray-200"
                  data-testid="close-location-modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search for a city, address, or place..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="location-search-input"
                />
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="location-search-button"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectSearchResult(result)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b last:border-b-0 text-sm"
                      data-testid={`search-result-${index}`}
                    >
                      {result.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Map */}
            <div className="h-80" data-testid="location-map">
              <MapContainer
                center={position || defaultCenter}
                zoom={position ? 13 : 6}
                style={{ height: '100%', width: '100%' }}
                key={position ? `${position[0]}-${position[1]}` : 'default'}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <LocationMarker 
                  position={position} 
                  setPosition={setPosition} 
                  setLocationName={setLocationName}
                  setAddressParts={setAddressParts}
                />
                {showRadius && position && (
                  <Circle
                    center={position}
                    radius={selectedRadius * 1000}
                    pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.1 }}
                  />
                )}
              </MapContainer>
            </div>

            {/* Radius Slider (if enabled) */}
            {showRadius && (
              <div className="px-4 py-3 border-t bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Radius: {selectedRadius} km
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={selectedRadius}
                  onChange={(e) => handleRadiusChange(parseInt(e.target.value))}
                  className="w-full"
                  data-testid="radius-slider"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>1 km</span>
                  <span>50 km</span>
                  <span>100 km</span>
                </div>
              </div>
            )}

            {/* Selected Location Display */}
            {locationName && (
              <div className="px-4 py-3 border-t bg-blue-50">
                <p className="text-sm text-gray-600">Selected Location:</p>
                <p className="font-medium text-gray-900">{locationName}</p>
                {position && (
                  <p className="text-xs text-gray-500">
                    Coordinates: {position[0].toFixed(4)}, {position[1].toFixed(4)}
                  </p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-3 border-t flex justify-between">
              <button
                onClick={handleClear}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                data-testid="clear-location-button"
              >
                Clear
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  data-testid="cancel-location-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!position || !locationName}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:shadow-lg disabled:opacity-50"
                  data-testid="confirm-location-button"
                >
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
